import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Plus, Search, Activity, Trash2 } from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ConsultationsPage() {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, consultation: null });
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    loadConsultations();
  }, []);

  const loadConsultations = async () => {
    try {
      const data = await api.getConsultations();
      setConsultations(data);
    } catch (error) {
      toast.error('Erro ao carregar consultas');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Rascunho', className: isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-700' },
      in_review: { label: 'Em Revisão', className: 'bg-secondary-light text-secondary' },
      approved: { label: 'Aprovado', className: 'bg-primary-light text-primary' },
      exported: { label: 'Exportado', className: 'bg-primary-light text-primary' }
    };
    return badges[status] || badges.draft;
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.consultation) return;
    try {
      await api.deleteConsultation(deleteDialog.consultation.id);
      toast.success('Rascunho excluído.');
      setDeleteDialog({ open: false, consultation: null });
      loadConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir rascunho');
    }
  };

  const filteredConsultations = consultations;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="consultations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-8">
          <div>
            <h1 className={`text-2xl sm:text-3xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>
              Consultas
            </h1>
            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Histórico de consultas e documentações</p>
          </div>
          <Button
            onClick={() => navigate('/consultations/new')}
            className="w-full sm:w-auto bg-primary hover:bg-primary-hover"
            data-testid="new-consultation-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nova Consulta
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            <Input
              type="text"
              placeholder="Buscar consultas..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-10 ${isDark ? 'bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500' : ''}`}
              data-testid="search-consultations-input"
            />
          </div>
        </div>

        {/* Consultations List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        ) : filteredConsultations.length === 0 ? (
          <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-xl border p-12 text-center`}>
            <Activity className={`h-16 w-16 ${isDark ? 'text-slate-600' : 'text-slate-300'} mx-auto mb-4`} />
            <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'} mb-4`}>
              {search ? 'Nenhuma consulta encontrada' : 'Nenhuma consulta registrada ainda'}
            </p>
            {!search && (
              <Button
                onClick={() => navigate('/consultations/new')}
                className="bg-primary hover:bg-primary-hover"
              >
                Criar primeira consulta
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredConsultations.map((consultation) => {
              const badge = getStatusBadge(consultation.status);
              const isDraft = ['draft', 'in_review'].includes(consultation.status);
              return (
                <div
                  key={consultation.id}
                  className={`${isDark ? 'bg-slate-800/50 border-slate-700 hover:border-primary/50' : 'bg-white border-slate-200 hover:border-primary/50'} p-6 rounded-xl border hover:shadow-md transition-all`}
                  data-testid={`consultation-card-${consultation.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => navigate(`/consultations/${consultation.id}`)}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-primary-light rounded-lg flex items-center justify-center">
                          <Activity className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Consulta Clínica
                          </h3>
                          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                            {consultation.created_at
                              ? format(new Date(consultation.created_at), "d 'de' MMMM 'de' yyyy, HH:mm", {
                                  locale: ptBR
                                })
                              : 'Data não disponível'}
                          </p>
                        </div>
                      </div>
                      {consultation.transcription && (
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'} line-clamp-2 mb-2`}>
                          {consultation.transcription.substring(0, 150)}...
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.className}`}>
                        {badge.label}
                      </span>
                      {isDraft && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 hover:text-red-700 border-red-200 hover:border-red-300 dark:border-red-800 dark:hover:border-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteDialog({ open: true, consultation });
                          }}
                          data-testid={`delete-draft-btn-${consultation.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Excluir rascunho
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Delete confirmation dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, consultation: deleteDialog.consultation })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Excluir rascunho</DialogTitle>
              <DialogDescription>
                Tem certeza? Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteDialog({ open: false, consultation: null })}
                data-testid="confirm-delete-cancel"
              >
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} data-testid="confirm-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
